import React, { FC, memo, useState } from 'react';
import classNames from 'classnames';

import { CustomListItem } from 'application/components/CustomList';
import { ModalTypes, useModalContext } from 'application/providers/ModalProvider';
import { useNotificationContext } from 'application/providers/NotificationProvider';
import { TEXTS } from 'application/pages/BillingPage/renderer/texts';

import { PaymentMethodObjectPureType } from 'domain/api/graphql/generated';

import useCardInformationViewModel from './useCardInformationViewModel';
import { BankCard, CardActions } from './components';
import { CardInformationProps } from './types';
import SberIDReauth from '../SberIDReauth';

import styles from './index.module.scss';

const CardInformation: FC<CardInformationProps> = ({
  paymentMethodId,
  isSelected,
  switcherHidden,
  setSelectedItemId,
}) => {
  const { openModal } = useModalContext();
  const notification = useNotificationContext();

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodObjectPureType | null>(null);
  const [isLinkingInProcess, setIsLinkingInProcess] = useState<boolean>(true);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState<boolean>(true);

  const {
    hasWaitPaymentLicenses,
    sberAuthButtonVisible,
    cardActionsLoading,
    onBindCardClick,
    onRebindCardClick,
    onRemoveCardClick,
  } = useCardInformationViewModel({
    setPaymentMethod: (paymentMethod: PaymentMethodObjectPureType | null) => {
      setPaymentMethod(paymentMethod);
    },
    setIsLinkingInProcess: (isLinkingInProcess: boolean) => {
      setIsLinkingInProcess(isLinkingInProcess);
    },
    setPaymentMethodLoading: (paymentMethodLoading: boolean) => {
      setPaymentMethodLoading(paymentMethodLoading);
    },
    openBillingBindCardModal: () => openModal(ModalTypes.BILLING_BIND_CARD, {}),
    openBillingRemoveCardModal: (afterClose: () => void) =>
      openModal(ModalTypes.BILLING_REMOVE_CARD, {
        afterClose,
      }),
    showBindErrorNotification: () =>
      notification.showErrorNotification({
        children: TEXTS.BILLING_BIND_CARD_NOTIFICATION_ERROR_MESSAGE,
      }),
  });

  return (
    <div className={styles.CardInformation}>
      <CustomListItem
        id={paymentMethodId}
        elementId={`payment_method_${paymentMethodId}`}
        contentId="card_payment"
        isSelected={isSelected}
        switcherHidden={switcherHidden}
        onChange={() => setSelectedItemId(paymentMethodId)}
        content={
          <BankCard
            className={classNames(styles.BankCard, {
              [styles.BankCardSelected]: isSelected,
            })}
            isLoading={paymentMethodLoading || isLinkingInProcess}
            paymentMethod={paymentMethod}
          />
        }
        footer={
          <>
            <CardActions
              paymentMethod={paymentMethod}
              isLinkingInProcess={paymentMethodLoading || isLinkingInProcess}
              cardActionsLoading={cardActionsLoading}
              hasWaitPaymentLicenses={hasWaitPaymentLicenses}
              onBindCardClick={onBindCardClick}
              onRebindCardClick={onRebindCardClick}
              onRemoveCardClick={onRemoveCardClick}
            />
            {sberAuthButtonVisible && !paymentMethodLoading && !isLinkingInProcess && <SberIDReauth />}
          </>
        }
      />
    </div>
  );
};

export default memo(CardInformation);
